/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty } from 'lodash'
import moment from 'moment'
import React, { useState, Fragment } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
  EuiButtonIcon,
  EuiFlexItem,
  EuiFlexGroup,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiToolTip,
  EuiPanel,
  EuiEmptyPrompt,
  EuiLink,
  EuiTablePagination,
  EuiFormHelpText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiTable } from '@modules/cui/Table'
import type { OnFilterChangeParams } from '@modules/cui/FilterContext'
import { getFilterQueryString, setFilterQueryString } from '@modules/cui/FilterContext'
import { CuiAlert } from '@modules/cui/Alert'
import { addToast } from '@modules/cui/Toasts'
import type { CuiTableColumn } from '@modules/cui/Table'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'
import Header from '@modules/cui/Header'
import { DocLink } from '@modules/doclinks'
import { useConfig } from '@modules/cui/ConfigContext'
import { useOrganizationId } from '@modules/permissions-lib/hooks'
import {
  useGetCurrentUserApiKeysQuery,
  useGetAllUsersApiKeysQuery,
  useDeleteCurrentUserApiKeyMutation,
  useDeleteUsersApiKeysMutation,
} from '@modules/api-keys-lib'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import { getOrganizationMembers } from '@/reducers/organizations'
import RoleBadges from '@/components/Organization/RoleBadges'
import { allUsersApiKeysCrumbs } from '@/lib/crumbBuilder'

import OrganizationMemberEmailLabel from '../Organization/OrganizationMembers/OrganizationMemberEmailLabel'
import { getMemberEmail, hasNoMember } from '../Organization/OrganizationMembers/lib'

import ApiKeysFilterContext from './ApiKeysFilterContext'
import RevokeKeyModal from './RevokeKeyModal'
import GenerateKeyModal from './GenerateKeyModal'
import GenerateApiKeyButton from './GenerateApiKeyButton'
import messages from './messages'
import ApiKeysRoleAssignments from './ApiKeysRoleAssignments'
import { ApiKeyStatus } from './ApiKeyStatus'
import { getKeyStatus } from './lib'

import type { ReduxState } from '@/types/redux'
import type { ApiKeyRow } from './types'
import type { FunctionComponent, ReactNode } from 'react'

const ApiKeys: React.FC = () => {
  const { regionId } = useParams<{ regionId?: string }>()
  const { organizationId } = useOrganizationId()
  const appName = useConfig('APP_NAME')
  const { formatMessage } = useIntl()
  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi

  // Determine if managing all users API keys
  const manageAllUsersApiKeys = regionId !== undefined
  const isAdminconsole = appName === 'adminconsole'

  const { data: currentUserApiKeys = [], isLoading: isLoadingCurrentUserKeys } =
    useGetCurrentUserApiKeysQuery({
      enabled: !manageAllUsersApiKeys,
    })
  const { data: allUsersApiKeys = [], isLoading: isLoadingAllUsersKeys } =
    useGetAllUsersApiKeysQuery({
      enabled: manageAllUsersApiKeys,
    })

  const organizationMembers = useSelector((state: ReduxState) =>
    getOrganizationMembers(state, organizationId),
  )

  const transformApiKeys = (rawApiKeys: any[]): ApiKeyRow[] =>
    rawApiKeys.map((apiKey) => ({
      ...apiKey,
      createdBy: getMemberEmail({ userId: apiKey.user_id, members: organizationMembers }),
      status: getKeyStatus(apiKey.expiration_date).status,
      isRemoved: isAdminconsole
        ? false
        : hasNoMember({ userId: apiKey.user_id, members: organizationMembers }),
    }))

  // Use appropriate API keys based on context and transform them
  const rawApiKeys = manageAllUsersApiKeys ? allUsersApiKeys : currentUserApiKeys
  const apiKeys = transformApiKeys(rawApiKeys)
  const isLoading = manageAllUsersApiKeys ? isLoadingAllUsersKeys : isLoadingCurrentUserKeys

  const deleteCurrentUserApiKeyMutation = useDeleteCurrentUserApiKeyMutation()
  const deleteUsersApiKeysMutation = useDeleteUsersApiKeysMutation()

  const [showRevokeKeyModal, setShowRevokeKeyModal] = useState(false)
  const [showGenerateKeyModal, setShowGenerateKeyModal] = useState(false)
  const [apiKeyToEditRoleAssignments, setApiKeyToEditRoleAssignments] = useState<ApiKeyRow | null>(
    null,
  )
  const [key, setKey] = useState<ApiKeyRow | null>(null)
  const [query, setQuery] = useState(getFilterQueryString({ storageKey: `ApiKeys` }))
  const [queryResults, setQueryResults] = useState<ApiKeyRow[]>(apiKeys)
  const [pageSize, setPageSize] = useState(100)
  const [pageIndex, setPageIndex] = useState(0)

  const showCreateApiKey = !manageAllUsersApiKeys
  const showRoleAssignments = !isAdminconsole // set API keys roles only in Userconsole

  const showRevokeKeyModalHandler = (apiKey: ApiKeyRow) => {
    setShowRevokeKeyModal(true)
    setKey(apiKey)
  }

  const closeRevokeKeyModal = () => {
    setShowRevokeKeyModal(false)
    setKey(null)
  }

  const confirmRevoke = () => {
    if (!key) {
      return
    }

    const revokeApiKeySuccess = {
      id: 'revokeKeySuccess',
      family: `api-key.revoke-key-success`,
      title: formatMessage(messages.revokeSuccess, {
        keyName: key.description,
      }),
      dataTestSubj: 'revoke-api-key.success',
      color: 'success',
    }

    const mutation = manageAllUsersApiKeys
      ? deleteUsersApiKeysMutation.mutateAsync({
          user_api_keys: [{ api_key_id: key.id, user_id: key.user_id || '' }],
        })
      : deleteCurrentUserApiKeyMutation.mutateAsync({ apiKeyId: key.id })

    return mutation
      .then(() => {
        closeRevokeKeyModal()
        addToast(revokeApiKeySuccess)
        return
      })
      .catch(() => {
        closeRevokeKeyModal()
        return
      })
  }

  const showGenerateKeyModalHandler = () => {
    setShowGenerateKeyModal(true)
  }

  const closeGenerateKeyModal = () => {
    setShowGenerateKeyModal(false)
  }

  const closeRoleAssignments = () => setApiKeyToEditRoleAssignments(null)

  const onChange = ({
    queryText,
    queryResults: newQueryResults,
  }: OnFilterChangeParams<ApiKeyRow>) => {
    setQuery(queryText)
    setQueryResults(newQueryResults)
    setFilterQueryString({ storageKey: `ApiKeys`, queryText })
    setPageIndex(0)
  }

  const pageSizeOptions = [25, 50, 100] as const

  const renderPagination = ({
    activePage,
    pageCount,
    onChangePage,
  }: {
    activePage: number
    pageCount: number
    onChangePage: (pageIndex: number) => void
  }) => {
    const totalCount = queryResults.length
    const showPagination = pageCount > 1
    const showMatchSummary = totalCount > 0

    if (!showMatchSummary && !showPagination) {
      return null
    }

    const startItem = activePage * pageSize + 1
    const endItem = Math.min((activePage + 1) * pageSize, totalCount)

    return (
      <Fragment>
        <EuiSpacer size='m' />

        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            {showMatchSummary && (
              <EuiFormHelpText>
                <FormattedMessage
                  id='api-keys.pagination.showing'
                  defaultMessage='Showing {start}-{end} of {total}'
                  values={{
                    start: startItem,
                    end: endItem,
                    total: totalCount,
                  }}
                />
              </EuiFormHelpText>
            )}
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiTablePagination
              aria-label='API keys pagination'
              pageCount={pageCount}
              activePage={activePage}
              onChangePage={(newPage) => {
                setPageIndex(newPage)
                onChangePage(newPage)
              }}
              itemsPerPage={pageSize}
              itemsPerPageOptions={[...pageSizeOptions]}
              onChangeItemsPerPage={(newPageSize) => {
                setPageSize(newPageSize)
                setPageIndex(0)
              }}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </Fragment>
    )
  }

  const renderApiKeyRoleAssignments = () => {
    if (!organizationId || apiKeyToEditRoleAssignments === null) {
      return null
    }

    return (
      <ApiKeysRoleAssignments
        organizationId={organizationId}
        apiKey={apiKeyToEditRoleAssignments}
        onClose={closeRoleAssignments}
        isUIAMEnabled={isUIAMEnabled}
      />
    )
  }

  const renderApiKeyName = (apiKey: ApiKeyRow): ReactNode => {
    if (!organizationId || !showRoleAssignments) {
      return apiKey.description
    }

    return (
      <PermissionsGate
        permissions={[
          {
            organizationId,
            type: 'role-assignment',
            action: 'update',
          },
        ]}
      >
        {({ hasPermissions }) =>
          hasPermissions ? (
            <EuiLink
              onClick={() => {
                setApiKeyToEditRoleAssignments(apiKey)
              }}
            >
              {apiKey.description}
            </EuiLink>
          ) : (
            apiKey.description
          )
        }
      </PermissionsGate>
    )
  }

  const getGridColumns = (): {
    initialSort: CuiTableColumn<ApiKeyRow>
    columns: Array<CuiTableColumn<ApiKeyRow>>
  } => {
    const expirationDateColumn = {
      label: formatMessage(messages.keyExpiresOnColumn),
      render: (apiKey) =>
        apiKey.expiration_date ? (
          <EuiToolTip
            position='right'
            content={moment(apiKey.expiration_date).format('MMMM Do YYYY, h:mm a')}
          >
            <div>{moment(apiKey.expiration_date).format('MMMM Do YYYY')}</div>
          </EuiToolTip>
        ) : (
          <FormattedMessage id='api-keys-table.expiration.never' defaultMessage='Never' />
        ),
      sortKey: `expiration_date`,
      width: '15%',
    }

    return {
      initialSort: expirationDateColumn,
      columns: [
        {
          label: formatMessage(messages.keyNameColumn),
          render: (apiKey) => renderApiKeyName(apiKey),
          sortKey: `description`,
          width: 'auto',
        },
        {
          label: formatMessage(messages.keyCreatedByColumn),
          render: ({ createdBy, isRemoved }) => (
            <OrganizationMemberEmailLabel email={createdBy} isRemoved={isRemoved} />
          ),
          sortKey: `createdBy`,
          width: 'auto',
        },
        {
          label: formatMessage(messages.keyStatus),
          render: (apiKey) => <ApiKeyStatus apiKey={apiKey} />,
          sortKey: (apiKey) => apiKey.status,
          truncateText: true,
          width: '10%',
        },
        expirationDateColumn,
        ...(showRoleAssignments
          ? [
              {
                label: <FormattedMessage id='api-key.roles' defaultMessage='Roles' />,
                render: (apiKey) => (
                  <RoleBadges
                    roleAssignments={apiKey.role_assignments}
                    organizationId={organizationId}
                  />
                ),
                width: 'auto',
              },
            ]
          : []),
        {
          label: formatMessage(messages.keyCreatedOnColumn),
          render: (apiKey) => (
            <EuiToolTip
              position='right'
              content={moment(apiKey.creation_date).format('MMMM Do YYYY, h:mm a')}
            >
              <div>{moment(apiKey.creation_date).format('MMMM Do YYYY')}</div>
            </EuiToolTip>
          ),
          sortKey: `creation_date`,
          width: '15%',
        },
        {
          label: formatMessage(messages.keyIdColumn),
          render: (apiKey) => apiKey.id,
          sortKey: `id`,
          width: '10%',
        },
        {
          mobile: {
            label: formatMessage(messages.keyActionsColumn),
          },
          label: formatMessage(messages.keyActionsColumn),
          actions: true,
          align: `center`,
          width: `8%`,
          render: (apiKey) => (
            <div data-test-id='revoke-api-key'>
              <EuiButtonIcon
                aria-label={formatMessage(messages.keyRevokeColumn)}
                onClick={() => showRevokeKeyModalHandler(apiKey)}
                iconType='trash'
                color='danger'
              />
            </div>
          ),
        },
      ],
    }
  }

  const renderKeysTable = () => {
    const { columns, initialSort } = getGridColumns()

    return (
      <EuiFlexGroup direction='column'>
        <EuiFlexItem>
          <EuiText grow={true} color='subdued'>
            <FormattedMessage
              id='api-keys.overview'
              defaultMessage='An API key allows you to perform most of the operations available in the UI console through API calls. You can create and manage deployments, configure remote clusters, set up traffic filters, manage extensions, and much more. {learnMore}'
              values={{
                learnMore: (
                  <DocLink link='apiKeysDocLink'>
                    <FormattedMessage
                      id='api-keys.overview.learn-more'
                      defaultMessage='Learn more'
                    />
                  </DocLink>
                ),
              }}
            />
          </EuiText>
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiFlexGroup justifyContent='flexEnd'>
            <EuiFlexItem>
              <ApiKeysFilterContext query={query} onChange={onChange} apiKeys={apiKeys} />
            </EuiFlexItem>

            {showCreateApiKey && (
              <EuiFlexItem grow={false}>
                <PermissionsGate
                  permissions={[
                    {
                      type: 'api-key',
                      action: 'create',
                    },
                  ]}
                >
                  {({ hasPermissions }) => (
                    <GenerateApiKeyButton
                      disabled={!hasPermissions}
                      onClick={showGenerateKeyModalHandler}
                    />
                  )}
                </PermissionsGate>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiPanel>
            <CuiTable<ApiKeyRow>
              initialSort={initialSort}
              initialLoading={isLoading}
              columns={columns}
              rows={isEmpty(queryResults) ? [] : queryResults}
              pageSize={pageSize}
              defaultPage={pageIndex}
              renderPagination={renderPagination}
            />
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  return (
    <Fragment>
      {regionId && (
        <Header
          name={<FormattedMessage id='keysManagement.title' defaultMessage='API keys' />}
          breadcrumbs={allUsersApiKeysCrumbs({ regionId })}
        />
      )}

      <PrivacySensitiveContainer>
        {renderApiKeyRoleAssignments()}

        {!isEmpty(apiKeys) || isLoading || !showCreateApiKey ? (
          renderKeysTable()
        ) : (
          <EmptyApiKeysTable onClick={showGenerateKeyModalHandler} />
        )}

        {deleteCurrentUserApiKeyMutation.error && (
          <Fragment>
            <EuiSpacer size='m' />
            <CuiAlert type='danger'>{deleteCurrentUserApiKeyMutation.error}</CuiAlert>
          </Fragment>
        )}

        {deleteUsersApiKeysMutation.error && (
          <Fragment>
            <EuiSpacer size='m' />
            <CuiAlert type='danger'>{deleteUsersApiKeysMutation.error}</CuiAlert>
          </Fragment>
        )}

        {showGenerateKeyModal && <GenerateKeyModal onCancel={closeGenerateKeyModal} />}
        {showRevokeKeyModal && (
          <RevokeKeyModal
            revokeApiKeyRequestInProgress={
              manageAllUsersApiKeys
                ? deleteUsersApiKeysMutation.isLoading
                : deleteCurrentUserApiKeyMutation.isLoading
            }
            keys={[key]}
            onCancel={closeRevokeKeyModal}
            onConfirm={confirmRevoke}
          />
        )}
      </PrivacySensitiveContainer>
    </Fragment>
  )
}

const EmptyApiKeysTable: FunctionComponent<{ onClick: () => void }> = ({ onClick }) => (
  <Fragment>
    <EuiSpacer size='l' />

    <EuiEmptyPrompt
      css={{ maxWidth: `50em` }}
      title={
        <h3>
          <FormattedMessage
            id='empty-api-keys-table.create-api-keys'
            defaultMessage='Create an API key'
            data-test-id='empty-api-keys-table.create-api-keys'
          />
        </h3>
      }
      color='plain'
      body={
        <EuiText>
          <p>
            <FormattedMessage
              id='api-keys.overview-description'
              defaultMessage='An API key allows you to perform most of the operations available in the UI console through API calls. You can create and manage deployments, configure remote clusters, set up traffic filters, manage extensions, and much more.'
            />
          </p>
        </EuiText>
      }
      actions={
        <PermissionsGate
          permissions={[
            {
              type: 'api-key',
              action: 'create',
            },
          ]}
        >
          {({ hasPermissions }) => (
            <GenerateApiKeyButton disabled={!hasPermissions} onClick={onClick} />
          )}
        </PermissionsGate>
      }
      footer={
        <Fragment>
          <EuiTitle size='xxs'>
            <span>
              <FormattedMessage
                id='api-keys.overview.want-learn-more'
                defaultMessage='Want to learn more?'
              />
            </span>
          </EuiTitle>
          &nbsp;&nbsp;
          <DocLink link='apiKeysDocLink' showExternalLinkIcon={true}>
            <FormattedMessage
              id='api-keys.overview.documentation'
              defaultMessage='Read documentation'
            />
          </DocLink>
        </Fragment>
      }
    />
  </Fragment>
)

export default ApiKeys
